'use client';

import React, { useState, useEffect } from 'react';
import { X, Sparkles, RefreshCw, Copy, Check, Loader2, MessageSquare, Zap, FileText } from 'lucide-react';
import { useChat } from '@/lib/hooks/useChat';
import { Contact } from './mockContacts';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface AIAssistantPanelProps {
  contact: Contact;
  onClose: () => void;
}

type ActiveTab = 'summary' | 'actions' | 'draft';

const OUTREACH_TEMPLATES = [
  {
    id: 'intro',
    name: 'Introduction',
    template: `Hi {{first_name}},

I came across your profile and noticed your work at {{company_name}} — really impressive what you're doing in the space.

I'll be at {{event_name}} and would love to connect briefly to share how we've been helping similar companies. Would you be open to a quick 15-minute chat?

Looking forward to connecting!`,
  },
  {
    id: 'followup',name: 'Follow-up After Event',
    template: `Hi {{first_name}},

It was great meeting you at {{event_name}}! I really enjoyed our conversation about {{company_name}}'s initiatives.

As promised, I wanted to follow up and share some resources that might be relevant. Would you have 20 minutes this week for a deeper dive?

Best regards`,
  },
  {
    id: 'meeting_request',
    name: 'Meeting Request',
    template: `Hi {{first_name}},

I hope this message finds you well. I've been following {{company_name}}'s growth and believe there's a strong alignment with what we offer.I'd love to schedule a brief call to explore potential synergies. Are you available for a 20-minute call next week?

Looking forward to hearing from you!`,
  },
  {
    id: 'linkedin_connect',
    name: 'LinkedIn Connection',
    template: `Hi {{first_name}}, I noticed we're both attending {{event_name}} and I'd love to connect. I work with companies like {{company_name}} on [your value prop]. Looking forward to connecting!`,
  },
];

function TabButton({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: React.ElementType; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
        active
          ? 'bg-primary text-white shadow-sm'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      }`}
    >
      <Icon size={12} />
      {label}
    </button>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-muted-foreground hover:text-foreground border border-border rounded-md hover:bg-muted transition-all"
    >
      {copied ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export default function AIAssistantPanel({ contact, onClose }: AIAssistantPanelProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('summary');
  const [selectedTemplate, setSelectedTemplate] = useState(OUTREACH_TEMPLATES[0].id);
  const [generatedContent, setGeneratedContent] = useState<Record<string, string>>({});
  const [loadingTab, setLoadingTab] = useState<ActiveTab | null>(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (response && !isLoading && loadingTab) {
      setGeneratedContent((prev) => ({ ...prev, [loadingTab]: response }));
      setLoadingTab(null);
    }
  }, [response, isLoading, loadingTab]);

  const contactContext = `
Contact: ${contact.firstName} ${contact.lastName}
Title: ${contact.title}
Company: ${contact.company}
Industry: ${contact.industry}
Country: ${contact.country}
Outreach Stage: ${contact.outreachStage}
LinkedIn Status: ${contact.linkedinStatus}
Meeting Status: ${contact.meetingStatus}
Priority: ${contact.priority} (Score: ${contact.priorityScore}/100)
Last Activity: ${contact.lastActivity}
Next Action Date: ${contact.nextActionDate}
Event: ${contact.event}
Tags: ${contact.tags.join(', ')}
Notes: ${contact.notes}
`.trim();

  const handleGenerateSummary = () => {
    setLoadingTab('summary');
    sendMessage([
      {
        role: 'system',
        content: 'You are a CRM AI assistant for a B2B sales team attending food industry events. Be concise, professional, and actionable. Use plain text only, no markdown headers or bullet symbols.',
      },
      {
        role: 'user',
        content: `Summarize the current outreach status for this contact in 3-4 sentences. Include where they are in the pipeline, what has been done, and what the current situation looks like.\n\n${contactContext}`,
      },
    ], { max_completion_tokens: 300 });
  };

  const handleGenerateActions = () => {
    setLoadingTab('actions');
    sendMessage([
      {
        role: 'system',
        content: 'You are a CRM AI assistant for a B2B sales team attending food industry events. Provide exactly 3 recommended next actions ranked by priority. Format each as: [PRIORITY] Action title: Brief explanation. Use HIGH, MEDIUM, or LOW for priority. Keep each action under 2 sentences.',
      },
      {
        role: 'user',
        content: `Based on this contact's current status, recommend the 3 most important next actions ranked by priority:\n\n${contactContext}`,
      },
    ], { max_completion_tokens: 400 });
  };

  const handleDraftMessage = () => {
    const template = OUTREACH_TEMPLATES.find((t) => t.id === selectedTemplate);
    if (!template) return;

    const filledTemplate = template.template
      .replace(/{{first_name}}/g, contact.firstName)
      .replace(/{{company_name}}/g, contact.company)
      .replace(/{{event_name}}/g, contact.event);

    setLoadingTab('draft');
    sendMessage([
      {
        role: 'system',
        content: 'You are a CRM AI assistant helping draft personalized outreach messages for a B2B sales team. Refine the provided template to be more personalized and compelling based on the contact context. Keep the same structure but make it feel genuine and specific. Return only the message text, no explanations.',
      },
      {
        role: 'user',
        content: `Personalize and improve this outreach message for the contact below. Make it specific to their role and company context.\n\nTemplate:\n${filledTemplate}\n\nContact Context:\n${contactContext}`,
      },
    ], { max_completion_tokens: 500 });
  };

  const currentContent = generatedContent[activeTab];
  const isCurrentLoading = isLoading && loadingTab === activeTab;

  const handleGenerate = () => {
    if (activeTab === 'summary') handleGenerateSummary();
    else if (activeTab === 'actions') handleGenerateActions();
    else handleDraftMessage();
  };

  const hasContent = !!currentContent;

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-[420px] bg-white border-l border-border shadow-2xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-gradient-to-r from-primary/5 to-violet-50">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Sparkles size={15} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">AI Assistant</p>
            <p className="text-[10px] text-muted-foreground">{contact.firstName} {contact.lastName} · {contact.company}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
        >
          <X size={14} />
        </button>
      </div>

      {/* Contact snapshot */}
      <div className="px-5 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-primary">{contact.firstName[0]}{contact.lastName[0]}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground">{contact.title}</p>
            <p className="text-[10px] text-muted-foreground truncate">{contact.company} · {contact.industry}</p>
          </div>
          <div className="text-right shrink-0">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold ${
              contact.priority === 'High' ? 'bg-red-100 text-red-700' :
              contact.priority === 'Medium'? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {contact.priority}
            </span>
            <p className="text-[10px] text-muted-foreground mt-0.5">Score: {contact.priorityScore}</p>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">{contact.outreachStage}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium">{contact.linkedinStatus}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Meeting: {contact.meetingStatus}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-4 py-3 border-b border-border">
        <TabButton active={activeTab === 'summary'} onClick={() => setActiveTab('summary')} icon={MessageSquare} label="Summary" />
        <TabButton active={activeTab === 'actions'} onClick={() => setActiveTab('actions')} icon={Zap} label="Next Actions" />
        <TabButton active={activeTab === 'draft'} onClick={() => setActiveTab('draft')} icon={FileText} label="Draft Message" />
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {/* Draft template selector */}
        {activeTab === 'draft' && (
          <div className="mb-4">
            <label className="block text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
              Template
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full text-xs border border-border rounded-lg px-3 py-2 bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              {OUTREACH_TEMPLATES.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <div className="mt-2 p-3 rounded-lg bg-muted/50 border border-border">
              <p className="text-[10px] font-semibold text-muted-foreground mb-1">Template Preview</p>
              <p className="text-[10px] text-muted-foreground whitespace-pre-line leading-relaxed line-clamp-4">
                {OUTREACH_TEMPLATES.find((t) => t.id === selectedTemplate)?.template
                  .replace(/{{first_name}}/g, contact.firstName)
                  .replace(/{{company_name}}/g, contact.company)
                  .replace(/{{event_name}}/g, contact.event)}
              </p>
            </div>
          </div>
        )}

        {/* Empty state / generate prompt */}
        {!hasContent && !isCurrentLoading && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-xl bg-primary/8 flex items-center justify-center mb-3">
              <Sparkles size={20} className="text-primary/60" />
            </div>
            <p className="text-sm font-semibold text-foreground mb-1">
              {activeTab === 'summary' && 'Generate Status Summary'}
              {activeTab === 'actions' && 'Get Recommended Actions'}
              {activeTab === 'draft' && 'Draft Outreach Message'}
            </p>
            <p className="text-xs text-muted-foreground max-w-[240px]">
              {activeTab === 'summary' && 'AI will analyze this contact\'s pipeline status and summarize the current situation.'}
              {activeTab === 'actions' && 'AI will recommend the 3 most impactful next steps ranked by priority.'}
              {activeTab === 'draft' && 'AI will personalize the selected template using this contact\'s context.'}
            </p>
          </div>
        )}

        {/* Loading state */}
        {isCurrentLoading && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Loader2 size={24} className="text-primary animate-spin mb-3" />
            <p className="text-xs text-muted-foreground">
              {activeTab === 'summary' && 'Analyzing contact status…'}
              {activeTab === 'actions' && 'Generating recommendations…'}
              {activeTab === 'draft' && 'Drafting personalized message…'}
            </p>
          </div>
        )}

        {/* Generated content */}
        {hasContent && !isCurrentLoading && (
          <div className="space-y-3">
            {activeTab === 'actions' ? (
              <ActionsList content={currentContent} />
            ) : (
              <div className="relative">
                <div className="p-4 rounded-xl bg-muted/40 border border-border">
                  <p className="text-xs text-foreground leading-relaxed whitespace-pre-line">{currentContent}</p>
                </div>
                <div className="flex justify-end mt-2">
                  <CopyButton text={currentContent} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-5 py-4 border-t border-border bg-muted/20">
        <button
          onClick={handleGenerate}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
        >
          {isCurrentLoading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Generating…
            </>
          ) : (
            <>
              {hasContent ? <RefreshCw size={14} /> : <Sparkles size={14} />}
              {hasContent ? 'Regenerate' : 'Generate with AI'}
            </>
          )}
        </button>
        {hasContent && (
          <p className="text-center text-[10px] text-muted-foreground mt-2">
            AI-generated content. Review before sending.
          </p>
        )}
      </div>
    </div>
  );
}

function ActionsList({ content }: { content: string }) {
  const lines = content.split('\n').filter((l) => l.trim());

  return (
    <div className="space-y-2.5">
      {lines.map((line, i) => {
        const isHigh = line.includes('[HIGH]') || line.toLowerCase().startsWith('high');
        const isMedium = line.includes('[MEDIUM]') || line.toLowerCase().startsWith('medium');
        const isLow = line.includes('[LOW]') || line.toLowerCase().startsWith('low');

        const priorityColor = isHigh
          ? 'border-red-200 bg-red-50'
          : isMedium
          ? 'border-amber-200 bg-amber-50'
          : isLow
          ? 'border-slate-200 bg-slate-50' :'border-border bg-muted/40';

        const dotColor = isHigh ? 'bg-red-500' : isMedium ? 'bg-amber-500' : isLow ? 'bg-slate-400' : 'bg-primary';

        const cleanLine = line
          .replace(/\[HIGH\]/gi, '')
          .replace(/\[MEDIUM\]/gi, '')
          .replace(/\[LOW\]/gi, '')
          .trim();

        return (
          <div key={i} className={`flex gap-3 p-3 rounded-xl border ${priorityColor}`}>
            <div className={`w-2 h-2 rounded-full shrink-0 mt-1 ${dotColor}`} />
            <p className="text-xs text-foreground leading-relaxed">{cleanLine}</p>
          </div>
        );
      })}
      <div className="flex justify-end">
        <CopyButton text={content} />
      </div>
    </div>
  );
}
